import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FC, useEffect, useRef, useState } from 'react';
import FavoritesButton from '@/components/common/layout/header-toolbar/favorites-button';
import MyCartButton from '@/components/common/layout/header-toolbar/my-cart-button';
import UserAuthToolbarSlot from '@/components/common/layout/user-button/user-auth-toolbar-slot';
import { Icon, IconButton, Link } from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useCoreConfig from '@/hooks/use-core-config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useScrollYForHeaderLayout } from '@/hooks/use-scroll-y-for-header-layout';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import FeatureFlag from '../../feature-flag/feature-flag';
import AnnouncementBanner from '../announcement-banner/announcement-banner';
import LanguageSelector from '../app-bar/language-selector/language-selector';
import MobileMenu from '../app-bar/mobile-menu/mobile-menu';
import MobileSearch from '../app-bar/mobile-search/mobile-search';
import NavigationBarV1 from '../app-bar/navigation-bar/navigation-bar-v1';
import SearchInput from '../secondary-app-bar/search-input/search-input';

const CenteredAppBar: FC = () => {
  const resolveThemeColor = useResolveThemeColor();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const stickyRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useKeycloak();
  const { features, logoSrc } = useCoreConfig();
  const scrollY = useScrollYForHeaderLayout(isStudioPreview, stickyRef);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isAgeVerified, setIsAgeVerified] = useState(true);

  useEffect(() => {
    setIsAgeVerified(sessionStorage.getItem('isAgeVerified') !== null);
  }, []);

  const headerContent = useHeaderContent();
  const parsedLogoHeight = Number(headerContent.logoHeight);
  const logoHeight =
    Number.isFinite(parsedLogoHeight) && parsedLogoHeight > 0 ? parsedLogoHeight : 40;
  const resolvedLogoSrc = headerContent.logoSrc || logoSrc || '/imgs/ibee-logo.png';
  const mainRowBackground = resolveThemeColor(
    headerContent.mainBackground ?? headerContent.background ?? 'content1',
  );
  const navigationBackground = resolveThemeColor(
    headerContent.background ?? headerContent.mainBackground ?? 'content1',
  );
  const searchChromeColor = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-0 left-0 w-full transition-shadow duration-300',
        scrollY > 30 ? 'shadow-md' : 'shadow-none',
        isAgeVerified ? 'z-50' : 'z-40',
      )}
    >
      <div className="flex flex-col">
        {headerContent.announcementBar ? <AnnouncementBanner /> : null}
        <div style={{ background: mainRowBackground }}>
          <div className="container mx-auto grid w-full grid-cols-[1fr_auto_1fr] items-center px-3 py-2 @lg:px-5 @lg:py-3">
            <div className="flex items-center justify-start gap-1">
              <MobileMenu />
              <FeatureFlag feature="globalSearch">
                <div className="relative hidden items-center @lg:flex">
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      isSearchOpen ? 'mr-2 w-[320px] opacity-100' : 'w-0 opacity-0',
                    )}
                  >
                    <SearchInput
                      ref={searchInputRef}
                      isVisible={isSearchOpen}
                      setIsSearchOpen={setIsSearchOpen}
                    />
                  </div>
                  <IconButton
                    aria-label="Search"
                    variant="light"
                    size="lg"
                    onPress={() => {
                      setIsSearchOpen(true);
                      setTimeout(() => searchInputRef.current?.focus(), 350);
                    }}
                    className={cn('px-2', isSearchOpen && 'hidden')}
                  >
                    <Icon
                      icon={faMagnifyingGlass}
                      style={{ color: searchChromeColor }}
                      fontSize={24}
                    />
                  </IconButton>
                  <IconButton
                    aria-label="Close search"
                    variant="light"
                    size="lg"
                    onPress={() => {
                      searchInputRef.current?.blur();
                      setIsSearchOpen(false);
                    }}
                    className={cn('px-2', !isSearchOpen && 'hidden')}
                  >
                    <Icon icon={faXmark} style={{ color: searchChromeColor }} fontSize={24} />
                  </IconButton>
                </div>
              </FeatureFlag>
            </div>

            <div
              className={cn(
                'flex justify-center px-2 transition-transform duration-300',
                scrollY > 30 ? 'scale-90' : 'scale-100',
              )}
            >
              <Link href="/">
                <div className="flex items-center" style={{ height: `${logoHeight}px` }}>
                  <RenderableImage
                    src={resolvedLogoSrc}
                    height={logoHeight}
                    className="block h-full w-auto max-w-[220px] cursor-pointer object-contain"
                    alt="logo"
                  />
                </div>
              </Link>
            </div>

            <div className="flex items-center justify-end gap-0 @sm:gap-0.5">
              <FeatureFlag feature="favorites">
                <FavoritesButton />
              </FeatureFlag>
              {(isAuthenticated || features.cartForGuests) && <MyCartButton />}
              <UserAuthToolbarSlot variant="primary" />
              <div className="hidden @lg:block">
                <LanguageSelector />
              </div>
            </div>
          </div>
        </div>

        <div style={{ background: navigationBackground }}>
          <div className="container mx-auto px-3 @lg:px-5">
            <NavigationBarV1 />
          </div>
        </div>

        <FeatureFlag feature="globalSearch">
          <MobileSearch />
        </FeatureFlag>
      </div>
    </div>
  );
};

export default CenteredAppBar;
